import { clsx } from 'clsx';
import { useId, useState } from 'react';

import type { KeyboardEvent, ReactNode } from 'react';

import styles from './Accordion.module.css';

import { Icon } from 'components/Icon';

export interface AccordionProps {
  /** Clickable title (always visible). */
  title: ReactNode;
  /** Main content (hidden when expanded is false). */
  content: ReactNode;
  /** Indicator if Accordion should be expanded by default.*/
  expanded?: boolean;
}

/** A controller for accordion display toggling of content with click on a title. */
export const Accordion = ({ title, content, expanded = false }: AccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const accordionId = useId();

  function onKeyUp(event: KeyboardEvent<HTMLDivElement>) {
    const { code } = event;

    if (code === 'Enter' || code === 'Space') {
      setIsExpanded(!isExpanded);
    }
  }

  return (
    <div className={styles['accordion']}>
      <div
        className={styles['accordion__title']}
        id={accordionId}
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyUp={onKeyUp}
        aria-expanded={isExpanded}
      >
        {title}
        <Icon
          className={clsx(styles['accordion__title-icon'], { [styles['accordion__title-icon--expanded']]: isExpanded })}
          name="CheveronLeft"
          size={16}
        />
      </div>
      <div
        aria-labelledby={accordionId}
        className={clsx(styles.accordion__content, { [styles['accordion__content--expanded']]: isExpanded })}
      >
        {content}
      </div>
    </div>
  );
};
